import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTitle,
} from "@/components/atoms/Dialog";
// import DynamicIcon from "@/components/atoms/DynamicIcon";
import { Cross2Icon } from "@radix-ui/react-icons";
import Video from "@/components/atoms/Video";
import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useState } from "react";

interface TestimonialItem {
  id: string;
  name: string;
  designation: string;
  description: string;
  image?: string;
  nameTextColor?: string;
  designationTextColor?: string;
  descriptionTextColor?: string;
}

const StudentTestimonialCard = ({
  data,
  cardColor,
  onClick,
  videoUrl,
  video,
  altImage,
}: {
  data: TestimonialItem;
  cardColor?: string;
  onClick?: () => void;
  videoUrl?: string;
  video?: string;
  altImage?: string;
}) => {
  const [isYouTubeModalOpen, setIsYouTubeModalOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoUrl) {
      setIsYouTubeModalOpen(true);
    } else if (video) {
      setIsVideoModalOpen(true);
    } else if (onClick) {
      onClick();
    }
  };

  const handleCloseYouTubeModal = () => {
    setIsYouTubeModalOpen(false);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl shadow-4",
        "xs:gap-3 xs:p-4",
        "sm:p-4 sm:gap-[18px] sm:items-center sm:justify-center",
        onClick && "cursor-pointer",
      )}
      style={
        {
          // @ts-ignore
          backgroundColor: cardColor || "#FFFFFF",
          "--name-color": data.nameTextColor || "#000000",
          "--designation-color": data.designationTextColor || "#666666",
          "--description-color": data.descriptionTextColor || "#6B7280",
        } as React.CSSProperties
      }
      onClick={onClick}
    >
      <div
        className={cn(
          "flex",
          "xs:gap-2 xs:flex-col xs:items-center xs:justify-center",
          "sm:gap-3 sm:flex-row sm:justify-center sm:items-center",
        )}
      >
        {data?.image && (
          <div
            className={cn(
              "relative shrink-0",
              "xs:w-[48px] xs:h-[48px]",
              "sm:w-[52px] sm:h-[52px]",
              "md:w-[52px] md:h-[52px]",
              (videoUrl || video) && "cursor-pointer",
            )}
            onClick={handleImageClick}
          >
            <Image
              src={data?.image || ""}
              alt={altImage || data.name}
              fill
              className="w-full h-full object-cover rounded-full"
            />
            {/* Play icon overlay when video is present */}
            {(videoUrl || video) && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full">
                <div className="bg-white/90 rounded-full p-1 flex items-center justify-center">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="ml-0.5"
                  >
                    <path
                      d="M8 5V19L19 12L8 5Z"
                      fill="currentColor"
                      className="text-gray-900"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "xs:text-center xs:text-heading-7-bold",
              "sm:text-heading-9-semibold sm:text-left ",
              "md:text-heading-6-bold",
              "lg:text-heading-4-bold",
              "xl:text-heading-3-semibold",
              "line-clamp-1",
              "type-h5",
            )}
            style={{ color: "var(--name-color)" }}
          >
            {data?.name}
          </div>
          <div
            className={cn(
              "xs:text-heading-9-regular xs:text-center",
              "sm:text-heading-9-regular sm:text-left",
              "md:text-heading-8-regular",
              "lg:text-heading-7-regular",
              "xl:text-heading-7-regular",
              "line-clamp-1",
              "type-body",
            )}
            style={{ color: "var(--designation-color)" }}
          >
            {data?.designation}
          </div>
        </div>
      </div>
      <div
        className={cn(
          "text-center",
          "xs:text-heading-9-regular",
          "sm:text-heading-9-regular",
          "md:text-heading-8-regular",
          "lg:text-heading-7-regular",
          "xl:text-heading-6-regular",
          "line-clamp-4",
          "type-body-sm",
        )}
        style={{ color: "var(--description-color)" }}
      >
        {data?.description}
      </div>

      {/* YouTube Video Overlay */}
      {videoUrl && (
        <YouTubeVideoOverlay
          isOpen={isYouTubeModalOpen}
          onClose={handleCloseYouTubeModal}
          videoUrl={videoUrl}
          title={data.name}
        />
      )}

      {/* Normal Video Overlay */}
      {video && (
        <Dialog open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
          <DialogOverlay className="bg-gray-900/90" />
          <DialogTitle />
          <DialogContent className="max-w-4xl w-[90vw] p-0 bg-black-100 rounded-lg">
            <div className="relative">
              <Button
                onClick={handleCloseVideoModal}
                className={cn(
                  "absolute w-14 h-10 -right-6 -top-10 z-50 rounded-full p-spacing-4 cursor-pointer",
                  "sm:w-14 sm:h-14",
                )}
                classType="ghost"
              >
                {/* <DynamicIcon className="w-4 h-4" iconName="close" /> */}
                <Cross2Icon className="w-4 h-4" />
              </Button>
              <div className="aspect-video w-full">
                <Video
                  src={video}
                  controls={true}
                  autoPlay={true}
                  title={data.name}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default StudentTestimonialCard;
